import type { ViewId } from "@/types";
import { VIEW_IDS } from "@/types";
import { markActiveNav, refreshIcons, scrollToTop } from "./dom";

type ViewListener = (view: ViewId) => void;

const listeners: ViewListener[] = [];
let current: ViewId = "inicio";
let bootstrapped = false;

export function currentView(): ViewId {
  return current;
}

/** Se ejecuta cada vez que cambia la vista (para refrescar sus datos). */
export function onViewChange(listener: ViewListener): void {
  listeners.push(listener);
}

export function isViewId(value: string): value is ViewId {
  return (VIEW_IDS as readonly string[]).includes(value);
}

const TITLES: Record<ViewId, string> = {
  inicio: "",
  servicios: "Servicios y precios",
  reservar: "Reservar hora",
  agenda: "Mis horas",
  contactos: "Agenda de clientes",
  contacto: "Contacto y ubicación",
};

const BASE_TITLE = "Yexzabel by Grace · Estética & Skin Care";

/** Navega a una vista: actualiza DOM, hash y notifica a los listeners. */
export function go(viewId: ViewId, options: { updateHash?: boolean } = {}): void {
  if (!isViewId(viewId)) return;

  const changed = current !== viewId;
  current = viewId;
  document.title = TITLES[viewId] ? `${TITLES[viewId]} · ${BASE_TITLE}` : BASE_TITLE;

  document.querySelectorAll<HTMLElement>(".view").forEach((section) => {
    section.classList.toggle("active", section.id === `view-${viewId}`);
  });
  markActiveNav(viewId);

  if (options.updateHash !== false && location.hash.slice(1) !== viewId) {
    location.hash = viewId;
  }

  scrollToTop();

  if (bootstrapped && changed) {
    focusViewHeading(viewId);
  }

  listeners.forEach((listener) => listener(viewId));
  refreshIcons();
  bootstrapped = true;
}

/** Lleva el foco al título de la vista (teclado y lectores de pantalla). */
function focusViewHeading(viewId: ViewId): void {
  const target =
    document.getElementById(`view-title-${viewId}`) ??
    document.querySelector<HTMLElement>(`#view-${viewId} h1`);
  target?.focus({ preventScroll: true });
}

/** Inicializa la vista según el hash y escucha atrás/adelante del navegador. */
export function initNavigation(): void {
  window.addEventListener("hashchange", () => {
    const hash = location.hash.slice(1);
    // Volver al inicio sin hash (p. ej. botón "atrás" desde la primera navegación).
    if (hash === "") {
      if (current !== "inicio") go("inicio", { updateHash: false });
      return;
    }
    if (isViewId(hash) && hash !== current) {
      go(hash, { updateHash: false });
    }
  });

  const initial = location.hash.slice(1);
  go(isViewId(initial) ? initial : "inicio", { updateHash: false });
}
